import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, ListItemButton, Typography } from '@mui/material';

import { caloTheme } from '../../assets/themes/calo';
import { chefText, focusRing, motion, tabular } from '../chefStyles';
import type { TaskDescription } from './describeTask';
import TaskTag from './TaskTag';

const { palette } = caloTheme;

type TaskRowProps = { description: TaskDescription; onOpen?: (() => void) | undefined; done?: boolean };

// The card surface of every Home entry. Content starts at the top, so a time beside it sits on the first line.
export const cardSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 2,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: '16px',
  backgroundColor: palette.neutral50,
  minHeight: { xs: 80, md: 96 },
  py: { xs: 2, md: 2.5 },
  px: { xs: 2, md: 3 },
  overflowWrap: 'anywhere'
} as const;

// One shape for every video review: the description fills the title, detail lines and tag; the row never moves between loads.
// Without `onOpen` it is the same card with nothing to press.
const TaskRow = ({ description: { title, details, note }, onOpen, done = false }: TaskRowProps) => {
  const body = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ ...chefText.title, color: done ? palette.neutral800 : palette.neutral900 }}>{title}</Typography>
      <Box sx={{ mt: 0.5 }}>
        {details.map((detail) => (
          <Typography key={detail} sx={{ ...chefText.body, color: palette.neutral800, ...tabular }}>
            {detail}
          </Typography>
        ))}
      </Box>
      {note !== undefined && <TaskTag>{note}</TaskTag>}
    </Box>
  );
  if (onOpen === undefined) {
    return <Box sx={cardSx}>{body}</Box>;
  }
  return (
    <ListItemButton
      onClick={onOpen}
      sx={{
        ...cardSx,
        pr: { xs: 1.5, md: 2 },
        ...motion,
        '&:hover': { backgroundColor: palette.neutral100 },
        '&.Mui-focusVisible': focusRing
      }}
    >
      {body}
      <ChevronRightIcon sx={{ alignSelf: 'center', flexShrink: 0, fontSize: { xs: 28, md: 32 }, color: palette.neutral800 }} />
    </ListItemButton>
  );
};

export default TaskRow;
